import React, { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import api from "@/lib/api";
import { ArrowLeft, ArrowRight, Layers, Sparkles } from "lucide-react";

export default function LearnCourse() {
  const { courseId } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    setLoading(true);
    api.get(`/learn/courses/${courseId}`).then((r) => {
      setData(r.data);
      setLoading(false);
    }).catch(() => setLoading(false));
  }, [courseId]);

  if (loading) return <div className="text-zinc-500 text-sm" data-testid="learn-course-loading">Loading course…</div>;
  if (!data) return <div className="text-red-300 text-sm">Course not found.</div>;

  const { course, units } = data;
  const comingSoon = !course.practice_bank_ready;

  // Group units by unit_group for Class 11/12 (which have official mark bands)
  const groups = [];
  const seen = new Set();
  for (const u of units) {
    const key = u.unit_group || "All Units";
    if (!seen.has(key)) {
      seen.add(key);
      groups.push({ name: key, units: [] });
    }
    groups.find((g) => g.name === key).units.push(u);
  }

  return (
    <div data-testid="learn-course-page">
      <Link
        to="/learn"
        className="inline-flex items-center gap-1.5 text-xs text-zinc-400 hover:text-yellow-300 mb-4"
        data-testid="learn-back-courses"
      >
        <ArrowLeft className="w-3.5 h-3.5" /> All courses
      </Link>

      <div className="mb-6 fade-up">
        <div className="flex flex-wrap items-center gap-2 mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] text-pink-300">
            CBSE · Class {course.grade}
          </div>
          {course.difficulty && (
            <div className={`text-[10px] uppercase tracking-[0.15em] px-2 py-0.5 rounded-full border ${
              course.difficulty === "standard"
                ? "border-emerald-300/40 text-emerald-300 bg-emerald-300/5"
                : "border-sky-300/40 text-sky-300 bg-sky-300/5"
            }`}>
              {course.difficulty === "standard" ? "041 · Standard" : "241 · Basic"}
            </div>
          )}
        </div>
        <h1 className="font-display text-3xl lg:text-4xl font-bold tracking-tight">{course.title}</h1>
        <p className="text-zinc-400 mt-2 max-w-2xl">{course.description}</p>
        {comingSoon && (
          <div className="mt-4 inline-flex items-center gap-2 text-xs text-orange-300 border border-orange-300/25 bg-orange-300/5 rounded-full px-3 py-1.5">
            <Sparkles className="w-3.5 h-3.5" />
            Practice bank in progress — units are viewable, question sets ship soon.
          </div>
        )}
      </div>

      <div className="space-y-8">
        {groups.map((g) => (
          <section key={g.name} data-testid={`learn-group-${g.name}`}>
            {g.name !== "All Units" && (
              <div className="flex items-center gap-3 mb-3">
                <div className="font-display text-base text-zinc-300">{g.name}</div>
                <div className="h-px flex-1 bg-white/5" />
                <Layers className="w-3.5 h-3.5 text-zinc-500" />
              </div>
            )}
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-3">
              {g.units.map((u, i) => (
                <Link
                  key={u.id}
                  to={`/learn/unit/${u.id}`}
                  data-testid={`learn-unit-${u.id}`}
                  className="fade-up lift rounded-lg border border-white/10 bg-[#152040] p-4 flex flex-col group hover:border-yellow-300/40 transition-colors"
                  style={{ animationDelay: `${(i % 6) * 40}ms` }}
                >
                  <div className="flex items-center justify-between mb-1.5">
                    <div className="text-[10px] font-mono text-zinc-500">Unit {u.number}</div>
                    {u.formative_only_notes?.length > 0 && (
                      <div className="text-[9px] uppercase tracking-[0.15em] text-yellow-300/80" title={u.formative_only_notes.join(", ")}>
                        Formative-only
                      </div>
                    )}
                  </div>
                  <div className="font-display text-base font-semibold leading-snug mb-2 text-zinc-100">
                    {u.title}
                  </div>
                  <div className="text-xs text-zinc-500 line-clamp-2 flex-1 min-h-[32px]">
                    {u.summary}
                  </div>
                  <div className="mt-3 pt-3 border-t border-white/5 flex items-center justify-end text-yellow-300 text-xs font-semibold group-hover:gap-1.5 gap-1 transition-all">
                    Open unit <ArrowRight className="w-3.5 h-3.5" />
                  </div>
                </Link>
              ))}
            </div>
          </section>
        ))}
      </div>
    </div>
  );
}
